import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { API_BASE_URL } from '../utils/apiConfig';
import './LeaderboardPage.css';

function LeaderboardPage() {
  const { t } = useTranslation();
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('day'); // day, week, month, allTime
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNext: false, hasPrev: false });
  const [copiedAddress, setCopiedAddress] = useState(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/hyperliquid/leaderboard?timeframe=${timeframe}&page=${page}&limit=${rowsPerPage}`);
      const data = await response.json();

      if (data.success) {
        setTraders(data.traders || []);
        setPagination(data.pagination || { total: 0, totalPages: 1, hasNext: false, hasPrev: false });
      } else {
        setError(data.error || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      setError('Failed to fetch leaderboard data');
      console.error('Leaderboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeframe, page, rowsPerPage]);

  useEffect(() => {
    fetchLeaderboard();
    // Refresh every 60 seconds
    const interval = setInterval(fetchLeaderboard, 60000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  // Reset page when timeframe or rowsPerPage changes
  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
    setPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyAddress = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';

    if (Math.abs(num) >= 1e9) {
      return `$${(num / 1e9).toFixed(2)}B`;
    }
    if (Math.abs(num) >= 1e6) {
      return `$${(num / 1e6).toFixed(2)}M`;
    }
    if (Math.abs(num) >= 1e3) {
      return `$${(num / 1e3).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0.00%';
    const num = parseFloat(value) * 100;
    if (isNaN(num)) return '0.00%';
    return `${num.toFixed(2)}%`;
  };

  const getTimeframeLabel = () => {
    switch (timeframe) {
      case 'day': return '24H';
      case 'week': return '7D';
      case 'month': return '30D';
      case 'allTime': return 'All Time';
      default: return '24H';
    }
  };

  // Calculate display range
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, pagination.total);

  return (
    <div className="leaderboard-page">
      <SEO page="leaderboard" />
      <Header />

      <div className="leaderboard-content">
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">{t('leaderboard.title', 'Leaderboard')}</h1>
          <p className="leaderboard-subtitle">
            {t('leaderboard.subtitle', 'Follow the most profitable and best crypto traders in real time, 24/7, via our live leaderboard.')}
          </p>
        </div>

        <div className="leaderboard-controls">
          <select
            className="timeframe-select"
            value={timeframe}
            onChange={(e) => handleTimeframeChange(e.target.value)}
          >
            <option value="day">24H</option>
            <option value="week">7D</option>
            <option value="month">30D</option>
            <option value="allTime">All Time</option>
          </select>
        </div>

        <div className="leaderboard-table-container">
          {loading ? (
            <div className="leaderboard-loading">
              <div className="spinner"></div>
              <p>{t('leaderboard.loading', 'Loading leaderboard...')}</p>
            </div>
          ) : error ? (
            <div className="leaderboard-error">
              <p>{error}</p>
              <button onClick={fetchLeaderboard}>{t('leaderboard.retry', 'Retry')}</button>
            </div>
          ) : (
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>{t('leaderboard.rank', 'Rank')}</th>
                  <th>{t('leaderboard.trader', 'Trader')}</th>
                  <th>{t('leaderboard.accountValue', 'Account Value')}</th>
                  <th>{t('leaderboard.pnl', `PNL (${getTimeframeLabel()})`)}</th>
                  <th>{t('leaderboard.roi', `ROI (${getTimeframeLabel()})`)}</th>
                  <th>{t('leaderboard.volume', `Volume (${getTimeframeLabel()})`)}</th>
                </tr>
              </thead>
              <tbody>
                {traders.map((trader, index) => {
                  const performance = trader.windowPerformances?.[timeframe] || {};
                  const pnl = parseFloat(performance.pnl || 0);
                  const roi = parseFloat(performance.roi || 0);
                  const volume = parseFloat(performance.vlm || 0);
                  const isPnlPositive = pnl >= 0;
                  const isRoiPositive = roi >= 0;

                  return (
                    <tr key={trader.ethAddress || index}>
                      <td className="rank-cell">{startIndex + index + 1}</td>
                      <td className="trader-cell">
                        <div className="trader-info">
                          {trader.displayName ? (
                            <>
                              <svg className="trader-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                              <span className="trader-name">{trader.displayName}</span>
                              <span
                                className={`trader-address-copy ${copiedAddress === trader.ethAddress ? 'copied' : ''}`}
                                onClick={() => copyAddress(trader.ethAddress)}
                                title={trader.ethAddress}
                              >
                                ({formatAddress(trader.ethAddress)})
                                {copiedAddress === trader.ethAddress && <span className="copy-tooltip">Copied!</span>}
                              </span>
                            </>
                          ) : (
                            <span
                              className={`trader-address copyable ${copiedAddress === trader.ethAddress ? 'copied' : ''}`}
                              onClick={() => copyAddress(trader.ethAddress)}
                              title={trader.ethAddress}
                            >
                              {formatAddress(trader.ethAddress)}
                              {copiedAddress === trader.ethAddress && <span className="copy-tooltip">Copied!</span>}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="value-cell">{formatCurrency(trader.accountValue)}</td>
                      <td className={`pnl-cell ${isPnlPositive ? 'positive' : 'negative'}`}>
                        {formatCurrency(pnl)}
                      </td>
                      <td className={`roi-cell ${isRoiPositive ? 'positive' : 'negative'}`}>
                        {formatPercent(roi)}
                      </td>
                      <td className="volume-cell">{formatCurrency(volume)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="leaderboard-footer">
          <p className="leaderboard-disclaimer">
            {t('leaderboard.disclaimer', 'Excludes accounts with less than 100k USDC account value and less than 10M USDC trading volume. ROI = PNL / max(100, starting account value + maximum net deposits) for the time window.')}
          </p>

          <div className="leaderboard-pagination">
            <div className="rows-per-page">
              <span>{t('leaderboard.rowsPerPage', 'Rows per page:')}</span>
              <select
                value={rowsPerPage}
                onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <span className="pagination-info">
              {startIndex + 1}-{endIndex} of {pagination.total}
            </span>

            <div className="pagination-buttons">
              <button
                className="pagination-btn"
                onClick={() => setPage(p => p - 1)}
                disabled={!pagination.hasPrev}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button
                className="pagination-btn"
                onClick={() => setPage(p => p + 1)}
                disabled={!pagination.hasNext}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LeaderboardPage;
